import { EventTypes, useEditor } from '@candulabs/craft';
import { connectWithHammock, ENDPOINTS } from 'hammock';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { CollectionContent, CollectionType } from '@candu-ai/collections/types';
import { useNodeType } from 'craft/hooks';
import { Collection } from 'craft/resolvers/Collection';
import { CollectionItem } from 'craft/resolvers/CollectionItem';
import { selectCollectionItemEvents } from 'craft/state';

const PlainName = ({ nodeId }: { nodeId: string }) => {
  const { query } = useEditor();
  const displayName = query.node(nodeId).displayName();
  const componentName = query.node(nodeId).get()?.data?.name;

  if (!componentName && !displayName) {
    return null;
  }
  return <span>{displayName || componentName}</span>;
};

interface CollectionProps {
  resource: CollectionType;
  onRenderMenu: () => void;
}

const CollectionDisplayNameImpl = ({ resource, onRenderMenu }: CollectionProps) => {
  useEffect(() => {
    onRenderMenu();
  }, []);

  return <span>Collection List: {resource.collectionName}</span>;
};

const CollectionDisplayName = connectWithHammock(({ collectionId }) => ({
  endpoint: ENDPOINTS.collections,
  id: collectionId,
}))(CollectionDisplayNameImpl);

interface CollectionItemProps {
  nodeId: string;
  resource: CollectionContent;
  collectionItemId: string;
  onRenderMenu: () => void;
}

const CollectionItemDisplayNameImpl = ({
  nodeId,
  resource,
  collectionItemId,
  onRenderMenu,
}: CollectionItemProps) => {
  const record = resource.records.find(({ id }) => id === collectionItemId);
  const primaryField = resource.schema.fields.find((field) => field.isPrimaryField);
  useEffect(() => {
    onRenderMenu();
  }, []);

  if (!primaryField || !record || !record.record[primaryField.id]) {
    return <PlainName nodeId={nodeId} />;
  }

  return <span>Collection Item: {record?.record[primaryField?.id]}</span>;
};

const CollectionItemDisplayName = connectWithHammock(({ collectionId }) => ({
  endpoint: ENDPOINTS.collectionContent,
  id: collectionId,
}))(CollectionItemDisplayNameImpl);

export const DisplayName = React.memo(
  ({ nodeId, onRenderMenu }: { nodeId: string; onRenderMenu: () => void }) => {
    const type = useNodeType(nodeId);
    const { query } = useEditor();
    const collectionSelectedItemId = useSelector(selectCollectionItemEvents)[EventTypes.selected];
    const getCollectionId = (nid: string) => query.node(nid)?.node?.data?.props?.collectionId;

    if (type === Collection) {
      const collectionId = getCollectionId(nodeId);
      return collectionId ? (
        <CollectionDisplayName
          nodeId={nodeId}
          collectionId={collectionId}
          onRenderMenu={onRenderMenu}
        />
      ) : (
        <PlainName nodeId={nodeId} />
      );
    }
    if (type === CollectionItem) {
      const parentId = query.node(nodeId)?.node?.data?.parent;
      const collectionId = parentId && getCollectionId(parentId);
      if (!collectionId || !collectionSelectedItemId) {
        return <PlainName nodeId={nodeId} />;
      }
      return (
        <CollectionItemDisplayName
          nodeId={nodeId}
          collectionItemId={collectionSelectedItemId}
          collectionId={collectionId}
          onRenderMenu={onRenderMenu}
        />
      );
    }

    return <PlainName nodeId={nodeId} />;
  },
);

DisplayName.displayName = 'DisplayName';
